import { validators } from '../utils/validate';
import { logger } from '../utils/logger';

// サイト固有の設定をインポート
import { SITE_CONFIG } from '@site/js/site-config';

// デフォルト設定（イミュータブル）
export const DEFAULT_CONFIG = Object.freeze({
  // 共通のデフォルト設定
  common: {
    configKey: '__TOPS_CONFIG__', // デフォルトのconfigKey
    theme: 'light',
    nav: 'vertical',
    layout: { mode: 'fluid' },
    topbar: { color: 'light' },
    menu: { color: 'dark' },
    sidenav: {
      toggle: 'sm-hover',
      size: 'default',
      user: false
    }
  },
  // サイト固有の設定をマージ
  site: SITE_CONFIG
});

// 現在のサイトのconfigKeyを取得
const CONFIG_KEY = DEFAULT_CONFIG.site.configKey || DEFAULT_CONFIG.common.configKey;

// HTML属性の取得と検証
const getValidatedAttribute = (element, attributeName, validator, defaultValue) => {
  const value = element.getAttribute(attributeName);
  return value && validator(value) ? value : defaultValue;
};

// 設定の保存
export const saveConfig = (config) => {
  try {
    sessionStorage.setItem(CONFIG_KEY, JSON.stringify(config));
    logger.info('Configuration saved');
    return true;
  } catch (error) {
    return logger.error('Failed to save configuration', error);
  }
};

// 保存された設定の読み込み
const loadSavedConfig = () => {
  try {
    const saved = sessionStorage.getItem(CONFIG_KEY);
    if (!saved || saved === 'undefined') return null;
    const config = JSON.parse(saved);
    return validators.validateConfig(config) ? config : null;
  } catch (error) {
    return logger.error('Failed to load saved configuration', error);
  }
};

// 設定の作成
const createConfig = (html) => {
  const attributes = {
    theme: getValidatedAttribute(html, 'data-bs-theme', validators.theme, DEFAULT_CONFIG.site.theme || DEFAULT_CONFIG.common.theme),
    nav: getValidatedAttribute(html, 'data-layout', (v) => ['topnav', 'vertical'].includes(v), DEFAULT_CONFIG.site.nav || DEFAULT_CONFIG.common.nav),
    layoutMode: getValidatedAttribute(html, 'data-layout-mode', validators.layoutMode, DEFAULT_CONFIG.site.layout?.mode || DEFAULT_CONFIG.common.layout.mode),
    topbarColor: getValidatedAttribute(html, 'data-topbar-color', validators.color, DEFAULT_CONFIG.site.topbar?.color || DEFAULT_CONFIG.common.topbar.color),
    sidenavSize: getValidatedAttribute(html, 'data-sidenav-size', validators.sidenavSize, DEFAULT_CONFIG.site.sidenav?.size || DEFAULT_CONFIG.common.sidenav.size),
    menuColor: getValidatedAttribute(html, 'data-menu-color', validators.color, DEFAULT_CONFIG.site.menu?.color || DEFAULT_CONFIG.common.menu.color)
  };

  return {
    ...DEFAULT_CONFIG.common,
    ...DEFAULT_CONFIG.site,
    theme: attributes.theme,
    nav: attributes.nav === 'topnav' ? 'horizontal' : 'vertical',
    layout: { 
      ...DEFAULT_CONFIG.common.layout,
      ...DEFAULT_CONFIG.site.layout,
      mode: attributes.layoutMode 
    },
    topbar: { 
      ...DEFAULT_CONFIG.common.topbar,
      ...DEFAULT_CONFIG.site.topbar,
      color: attributes.topbarColor 
    },
    menu: { 
      ...DEFAULT_CONFIG.common.menu,
      ...DEFAULT_CONFIG.site.menu,
      color: attributes.menuColor 
    },
    sidenav: { 
      ...DEFAULT_CONFIG.common.sidenav,
      ...DEFAULT_CONFIG.site.sidenav,
      size: attributes.sidenavSize 
    }
  };
};

// 設定の初期化
export const initConfig = () => {
  const html = document.documentElement;
  
  try {
    let config = createConfig(html);
    let theme = config.theme;
    const savedConfig = loadSavedConfig();
    if (savedConfig) {
      config = { ...config, ...savedConfig };
    }

    // テーマ変更の検出（savedConfigがnullの場合も考慮）
    const previousTheme = theme;
    const currentTheme = config.theme;
    
    // イベントの発行をwindow.onloadまで遅延
    if (previousTheme !== currentTheme) {
      const themeChangedEvent = new CustomEvent('themeChanged', {
        detail: {
          theme: currentTheme,
          previousTheme: previousTheme,
          config: config,
          isInitialLoad: !savedConfig
        },
        bubbles: true
      });
      
      // window.onloadまで遅延
      if (document.readyState === 'loading') {
        window.addEventListener('load', () => {
          document.dispatchEvent(themeChangedEvent);
        });
      } else {
        // すでにload完了している場合は即座に発行
        document.dispatchEvent(themeChangedEvent);
      }
    }

    // グローバル設定の更新
    window.defaultConfig = { ...config };
    window.config = config;

    logger.info('Configuration initialized');
    return config;
  } catch (error) {
    logger.error('Failed to initialize configuration', error);
    return DEFAULT_CONFIG;
  }
}; 